import { useState, useCallback } from "react";
import { RefreshCw, Share2 } from "lucide-react";

const questions = [
  {
    q: "It's 2 AM. You're still awake because…",
    opts: [
      { text: "Overthinking a conversation from 2019", type: "overthinker" },
      { text: "Building something nobody asked for", type: "builder" },
      { text: "Deep in a Wikipedia rabbit hole about fungi", type: "philosopher" },
      { text: "Making the perfect playlist for tomorrow's walk", type: "cupcake" },
    ],
  },
  {
    q: "Your friend cancels plans. You…",
    opts: [
      { text: "Feel secretly relieved", type: "philosopher" },
      { text: "Immediately fill the time with a project", type: "builder" },
      { text: "Spiral about whether they actually like you", type: "overthinker" },
      { text: "Order food and watch a documentary", type: "cupcake" },
    ],
  },
  {
    q: "Someone gives you unsolicited life advice. You…",
    opts: [
      { text: "Smile, nod, internally combust", type: "overthinker" },
      { text: "Ask them 14 follow-up questions", type: "philosopher" },
      { text: "Immediately do the opposite", type: "builder" },
      { text: "Offer them a snack to change the subject", type: "cupcake" },
    ],
  },
  {
    q: "What's your relationship with \"hustle culture\"?",
    opts: [
      { text: "Tried it. Burned out. Got the t-shirt.", type: "builder" },
      { text: "I think about it more than I do it", type: "overthinker" },
      { text: "I wrote a 3,000-word journal entry about why it's toxic", type: "philosopher" },
      { text: "I hustle exclusively toward nap time", type: "cupcake" },
    ],
  },
  {
    q: "Pick your ideal Friday night in Valencia:",
    opts: [
      { text: "Deep conversation with strangers on a rooftop", type: "philosopher" },
      { text: "Whiteboarding my next idea at midnight", type: "builder" },
      { text: "Journaling with wine (or without)", type: "overthinker" },
      { text: "Cooking for 12 people I barely know", type: "cupcake" },
    ],
  },
];

const results = {
  overthinker: {
    title: "The Beautiful Overthinker",
    desc: "You feel everything twice — once when it happens and again at 3 AM. Your depth is your superpower, even when it doesn't feel like it.",
  },
  builder: {
    title: "The Chaos Architect",
    desc: "You turn disorder into something real. While others plan, you ship. Messy, imperfect, and absolutely alive.",
  },
  philosopher: {
    title: "The Sober Sage",
    desc: "You see patterns everyone else misses. You'd rather understand life than optimize it. The frogs respect you.",
  },
  cupcake: {
    title: "The Cupcake Diplomat",
    desc: "You bring the warmth. You bring the snacks. You're the reason people actually stay at the party. Community lives because of you.",
  },
};

export const ChaosQuiz = () => {
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  const handleAnswer = useCallback(
    (type) => {
      const next = [...answers, type];
      setAnswers(next);

      if (next.length === questions.length) {
        const counts = {};
        next.forEach((t) => (counts[t] = (counts[t] || 0) + 1));
        const winner = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
        setResult(results[winner]);
        setStep(questions.length);
      } else {
        setStep(next.length);
      }
    },
    [answers]
  );

  const reset = () => {
    setStep(-1);
    setAnswers([]);
    setResult(null);
  };

  const shareResult = () => {
    if (!result) return;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      `I'm "${result.title}" — what kind of chaos are you? Take the quiz at tccpodcast.com 🧁`
    )}`;
    window.open(url, "_blank");
  };

  return (
    <section
      className="px-5 sm:px-8 lg:px-12 py-14 sm:py-20 md:py-32 bg-[#0C0C14] border-y border-[#232332]"
      data-testid="chaos-quiz-section"
    >
      <div className="max-w-3xl">
        {/* Intro */}
        {step === -1 && (
          <div data-testid="quiz-intro">
            <p className="overline-tcc mb-4">Interactive — 5 questions, zero judgment</p>
            <h2 className="font-display text-4xl sm:text-6xl md:text-7xl leading-[0.9] text-[#F4F2EE] mb-6 break-words">
              What kind of<br />
              <span className="text-stroke-pink">chaos</span> are you?
            </h2>
            <p className="text-[#9494A6] font-light text-base md:text-lg mb-10 max-w-md">
              Find your chaos archetype and share it with the world.
            </p>
            <button onClick={() => setStep(0)} className="btn-brutal" data-testid="quiz-start-btn">
              Take the quiz
            </button>
          </div>
        )}

        {/* Questions */}
        {step >= 0 && step < questions.length && (
          <div data-testid={`quiz-question-${step}`}>
            {/* Progress */}
            <div className="flex items-center gap-1 mb-8">
              {questions.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 transition-colors duration-300 ${
                    i <= step ? "bg-[#FF3E8A]" : "bg-[#232332]"
                  }`}
                />
              ))}
            </div>

            <p className="font-mono-tcc text-xs uppercase tracking-[0.25em] text-[#5E5E72] mb-4">
              Q.{step + 1} / {questions.length}
            </p>
            <h3 className="font-display text-2xl sm:text-4xl md:text-5xl leading-none text-[#F4F2EE] mb-10 break-words">
              {questions[step].q}
            </h3>

            <div className="border-t border-[#232332]">
              {questions[step].opts.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => handleAnswer(opt.type)}
                  className="w-full flex items-baseline gap-4 sm:gap-5 text-left px-3 sm:px-6 py-5 border-b border-[#232332] bg-transparent hover:bg-[#C8FF3E] group transition-colors duration-150"
                  data-testid={`quiz-option-${step}-${i}`}
                >
                  <span className="font-mono-tcc text-xs text-[#FF3E8A] group-hover:text-[#08080D] shrink-0 transition-colors duration-150">
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="text-base md:text-lg font-light text-[#F4F2EE] group-hover:text-[#08080D] transition-colors duration-150">
                    {opt.text}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div data-testid="quiz-result">
            <div className="border border-[#FF3E8A] bg-[#FF3E8A] p-8 sm:p-12 md:p-14 mb-8">
              <p className="font-mono-tcc text-[10px] uppercase tracking-[0.25em] text-[#08080D]/60 mb-4">
                Diagnosis complete — you are
              </p>
              <h3 className="font-display text-3xl sm:text-5xl md:text-6xl leading-none text-[#08080D] mb-5 break-words">
                {result.title}
              </h3>
              <p className="text-[#08080D]/80 text-base md:text-lg font-light leading-relaxed max-w-xl">
                {result.desc}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <button onClick={shareResult} className="btn-brutal" data-testid="quiz-share-btn">
                <Share2 size={14} />
                Share result
              </button>
              <button onClick={reset} className="btn-brutal-ghost" data-testid="quiz-retake-btn">
                <RefreshCw size={14} />
                Retake
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
